'use client';

import { Swiper, SwiperSlide } from 'swiper/react';
import { Pagination, Scrollbar } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/scrollbar';
import Image from 'next/image';
import { Heading } from '@/components/ui/Heading';
import Link from 'next/link';
import { SwiperListProps } from '@/core/models/slider-model';
import { useDictionary } from '@/core/hooks/use-dictionary';
import CustomButton from './CustomButton';

// اگه تعداد آیتم‌ها کمتر یا مساوی این عدد باشه، دیگه به سوییپرِ
// کارتی نیازی نیست: به‌جاش یه گرید می‌ذاریم که آیتم‌ها به‌صورت
// مساوی عرض ردیف رو پر می‌کنن (سوییپر با اسلاید ثابت ۳۰۰px برای
// تعداد کم، فضای خالی/چیدمان نامتقارن ایجاد می‌کرد).
const MAX_VISIBLE = 5;
const MIN_ITEM_WIDTH = 240;

export default function SwiperList({ data }: SwiperListProps) {
  const { dictionary } = useDictionary();

  const items = Array.isArray(data.items)
    ? data.items
    : Array.isArray(data.sliderItems)
      ? data.sliderItems
      : [];

  const useFluidGrid = items.length > 0 && items.length <= MAX_VISIBLE;

  const renderCard = (item: (typeof items)[number]) => (
    <div className="relative w-full aspect-[5/6]">
      {(item.image?.url || item.image_info?.url) && (
        <Image
          src={item.image?.url || item.image_info?.url}
          alt={item.title || ''}
          fill
          className="object-cover rounded-md"
        />
      )}
      <div className="absolute inset-0 z-0 bg-linear-to-t rounded-md from-black/40 to-white/0" />
      <div className="absolute bottom-3 right-3 left-3">
        <Heading className="text-primary-0" level={4}>{item.title}</Heading>
        <p className="text-primary-100">{item.description}</p>
      </div>
    </div>
  );

  return (
    <div className="w-full col-span-12">
      <div className="flex justify-between items-center mb-10 max-md:flex-col max-md:items-start gap-4">
        <div>
          <Heading level={3}>{data.title}</Heading>
          <p className="text-primary-600">{data.description}</p>
        </div>
        {data.link && (
          <CustomButton>
            <Link href={data.link}>
              {dictionary.ui?.pages.seeAll}
            </Link>
          </CustomButton>
        )}
      </div>

      {useFluidGrid ? (
        <div
          className="grid gap-4"
          style={{ gridTemplateColumns: `repeat(auto-fit, minmax(${MIN_ITEM_WIDTH}px, 1fr))` }}
        >
          {items.map((item, index) => (
            <div key={index}>
              {item.link ? <Link href={item.link}>{renderCard(item)}</Link> : renderCard(item)}
            </div>
          ))}
        </div>
      ) : (
        <Swiper
          modules={[Pagination]}
          spaceBetween={16}
          slidesPerView="auto"
          pagination={{ clickable: true }}
          className="w-full"
          style={{
            paddingBottom: "50px",
            "--swiper-bullet-width": "0.5rem",
            "--swiper-bullet-width-active": "1.5rem",
            "--swiper-bullet-height": "0.5rem",
            "--swiper-pagination-color": "var(--color-secondary-200)",
            "--swiper-pagination-color-active": "var(--color-secondary-200)",
          } as React.CSSProperties}
        >
          {items.map((item, index) => (
            <SwiperSlide key={index} style={{ width: '300px', height: '360px' }}>
              {item.link ? <Link href={item.link}>{renderCard(item)}</Link> : renderCard(item)}
            </SwiperSlide>
          ))}
        </Swiper>
      )}
    </div>
  );
}